import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { 
  Briefcase, CheckCircle, Clock, DollarSign, Calendar, 
  User, Shield, LogOut, FileText, Send, CheckSquare, Wrench
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Job {
  id: string;
  title: string;
  property: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed';
  price: number;
  dueDate: string;
  client: string;
}

export default function ContractorPortal() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { currentUser, logout } = useStore();
  const [activeTab, setActiveTab] = useState<'jobs' | 'invoices' | 'settings'>('jobs');
  const [loading, setLoading] = useState(false);

  // Mock Contractor Jobs (High-fidelity)
  const [jobs, setJobs] = useState<Job[]>([
    {
      id: 'job-1',
      title: 'AC Unit Repair & Service',
      property: '124 Pine Crest Rd, Austin, TX',
      description: 'System blowing warm air. Needs compressor replacement and coolant flush.',
      status: 'in_progress',
      price: 450,
      dueDate: '2026-06-10',
      client: 'Metro Property Management'
    },
    {
      id: 'job-2',
      title: 'Drywall Repair & Paint',
      property: '809 West Ave Apt 4B, Austin, TX',
      description: 'Patch hole in master bedroom and paint wall to match existing color.',
      status: 'pending',
      price: 250,
      dueDate: '2026-06-14',
      client: 'Sarah Jenkins (Tenant)'
    },
    {
      id: 'job-3',
      title: 'Kitchen Sink Faucet Replacement',
      property: '2204 Holly St, Austin, TX',
      description: 'Install brand new commercial-grade gooseneck faucet. Contractor supplied.',
      status: 'completed',
      price: 180,
      dueDate: '2026-06-02',
      client: 'Metro Property Management'
    }
  ]);

  const handleUpdateStatus = (jobId: string, newStatus: 'pending' | 'in_progress' | 'completed') => {
    setJobs(prev => prev.map(job => job.id === jobId ? { ...job, status: newStatus } : job));
    toast.success(`Job status updated to ${newStatus.replace('_', ' ')}`);
  };

  const handleSignOut = async () => {
    if (supabase) await supabase.auth.signOut();
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#07080d] text-white flex flex-col font-sans select-none animate-fade-in">
      {/* Premium Background Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
      
      {/* Premium Header */}
      <header className="sticky top-0 z-50 bg-[#07080d]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
            <Wrench className="text-purple-400 w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wider uppercase italic">Contractor Portal</h1>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-black">Powered by WholeScale OS</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col text-right">
            <span className="text-sm font-bold">{currentUser?.name || 'Contractor'}</span>
            <span className="text-[10px] text-purple-400 uppercase font-black tracking-widest">Verified Contractor</span>
          </div>
          <button 
            onClick={handleSignOut}
            className="p-2.5 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8 flex flex-col md:flex-row gap-8 relative z-10">
        {/* Left Navigation Bar */}
        <aside className="w-full md:w-64 shrink-0 space-y-4">
          <div className="bg-zinc-900/40 border border-white/5 rounded-3xl p-6 space-y-6">
            <div>
              <p className="text-[10px] font-black uppercase text-zinc-500 tracking-wider mb-2">My Profile</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center font-black text-white italic">
                  {currentUser?.name?.slice(0, 2).toUpperCase() || 'C'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{currentUser?.name}</p>
                  <p className="text-[10px] text-zinc-400 truncate">{currentUser?.email}</p>
                </div>
              </div>
            </div>

            <div className="h-px bg-white/5" />

            <div className="space-y-1">
              {[
                { id: 'jobs', label: 'Active Jobs', icon: Briefcase },
                { id: 'invoices', label: 'Invoices', icon: FileText },
                { id: 'settings', label: 'Portal Settings', icon: User }
              ].map(tab => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wider text-left transition-all ${
                      active
                        ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white border border-transparent'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Right Content Panel */}
        <section className="flex-1 space-y-6">
          {activeTab === 'jobs' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold uppercase italic tracking-wider">Active Jobs</h2>
                  <p className="text-xs text-zinc-500 mt-1">Review assignments, update status, and manage tasks.</p>
                </div>
              </div>

              <div className="space-y-4">
                {jobs.map(job => (
                  <div key={job.id} className="p-6 bg-zinc-900/30 border border-white/5 rounded-3xl hover:border-purple-500/20 transition-all flex flex-col gap-4 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/5 rounded-full blur-2xl group-hover:bg-purple-500/10 transition-all" />
                    
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <h3 className="text-base font-bold text-white leading-tight">{job.title}</h3>
                        <p className="text-xs text-purple-400 font-semibold mt-1">{job.property}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {job.status === 'completed' && (
                          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider">
                            <CheckCircle size={12} /> Completed
                          </span>
                        )}
                        {job.status === 'in_progress' && (
                          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-black uppercase tracking-wider animate-pulse">
                            <Clock size={12} /> In Progress
                          </span>
                        )}
                        {job.status === 'pending' && (
                          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black uppercase tracking-wider">
                            <Clock size={12} /> Pending
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-zinc-400 leading-relaxed max-w-3xl">{job.description}</p>

                    <div className="h-px bg-white/5" />

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-1.5 text-zinc-500 text-xs font-medium">
                          <DollarSign size={14} className="text-zinc-400" />
                          <span className="text-white font-bold">${job.price}</span> (Payout)
                        </div>
                        <div className="flex items-center gap-1.5 text-zinc-500 text-xs font-medium">
                          <Calendar size={14} className="text-zinc-400" />
                          Due: <span className="text-white font-bold">{job.dueDate}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {job.status !== 'completed' && (
                          <>
                            {job.status === 'pending' && (
                              <button 
                                onClick={() => handleUpdateStatus(job.id, 'in_progress')}
                                className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-xs font-bold uppercase tracking-wider transition-all"
                              >
                                Start Work
                              </button>
                            )}
                            {job.status === 'in_progress' && (
                              <button 
                                onClick={() => handleUpdateStatus(job.id, 'completed')}
                                className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-xs font-bold uppercase tracking-wider transition-all"
                              >
                                Mark Completed
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'invoices' && (
            <div className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 space-y-6 text-center max-w-2xl mx-auto">
              <FileText size={48} className="mx-auto text-purple-500/40" />
              <div>
                <h3 className="text-lg font-bold">Billing & Invoices</h3>
                <p className="text-xs text-zinc-400 mt-2">Create invoices for your completed jobs to receive fast payouts.</p>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 text-left space-y-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-zinc-400">Total Billed YTD:</span>
                  <span className="font-bold text-white">$1,890.00</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-zinc-400">Pending Approvals:</span>
                  <span className="font-bold text-purple-400">$180.00</span>
                </div>
              </div>
              <button 
                onClick={() => toast.success("Invoice generated successfully!")}
                className="px-6 py-3 rounded-2xl bg-purple-600 hover:bg-purple-500 text-xs font-bold uppercase tracking-widest transition-all"
              >
                Create New Invoice
              </button>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 space-y-6 max-w-2xl mx-auto">
              <h3 className="text-lg font-bold uppercase italic tracking-wider">Portal Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-black uppercase text-zinc-500 tracking-widest block mb-1">Company / DBA</label>
                  <input 
                    type="text" 
                    defaultValue="Jenkins Remodeling & Construction" 
                    className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500" 
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase text-zinc-500 tracking-widest block mb-1">Tax ID / EIN</label>
                  <input 
                    type="text" 
                    defaultValue="XX-XXX4930" 
                    className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-500" 
                  />
                </div>
                <button 
                  onClick={() => toast.success("Settings saved successfully!")}
                  className="px-6 py-3 rounded-2xl bg-purple-600 hover:bg-purple-500 text-xs font-bold uppercase tracking-widest transition-all"
                >
                  Save Settings
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
